import { getLocalHubCredential } from './hubCredentialStore';
import { pairWithHub, postToHub } from './hubPairingService';
import { targetAddressSpaceForUrl } from './hubDiscoveryService';

let scopedSession = null;
let activeLocalRequests = 0;
// Recovery endpoints may upload several batches and ask cloud to build a
// manifest.  Ten seconds is appropriate for normal reads, but it aborts a
// valid recovery request while the Hub is still working.
const RECOVERY_REQUEST_TIMEOUT_MS = 120000;

async function credentialFor(store, discovery, refreshGrant = false) {
  let credential = await getLocalHubCredential(discovery?.profile?.hub_uuid);
  if (!credential || refreshGrant) {
    credential = await pairWithHub(
      store, discovery, store.state.device.device?.name || 'Dispositivo EWU',
    );
  }
  return credential;
}

async function tokenFor(store, discovery, scopes, refreshGrant = false) {
  const key = `${discovery.profile.hub_uuid}:${[...scopes].sort().join(',')}`;
  if (scopedSession?.key === key && scopedSession.expiresAt > Date.now() + 5000) {
    return scopedSession.token;
  }
  const credential = await credentialFor(store, discovery, refreshGrant);
  const response = await postToHub(
    `${discovery.url}/local/v1/sessions`,
    {
      local_device_id: credential.local_device_id,
      local_device_secret: credential.local_device_secret,
      scopes,
    },
    5000,
  );
  scopedSession = {
    key,
    token: response.session_token,
    expiresAt: new Date(response.expires_at).getTime(),
  };
  return scopedSession.token;
}

function requestUrl(discovery, config) {
  const url = new URL(`${discovery.url}${config.url}`);
  Object.entries(config.params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      url.searchParams.set(key, String(value));
    }
  });
  return url.toString();
}

async function fetchLocalHub(discovery, config, token) {
  const url = requestUrl(discovery, config);
  const method = String(config.method || 'get').toUpperCase();
  const controller = new AbortController();
  const timeout = window.setTimeout(
    () => controller.abort(),
    Number(config.timeout || 10000),
  );
  try {
    const headers = {
      ...(config.data === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(config.headers || {}),
      Authorization: `Bearer ${token}`,
    };
    const response = await fetch(url, {
      method,
      credentials: 'omit',
      mode: 'cors',
      signal: controller.signal,
      targetAddressSpace: targetAddressSpaceForUrl(url),
      headers,
      ...(
        config.data === undefined || ['GET', 'HEAD'].includes(method)
          ? {}
          : { body: JSON.stringify(config.data) }
      ),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const detail = data?.detail;
      const message = typeof detail === 'object'
        ? detail.message || detail.error || detail.code
        : detail;
      const error = new Error(
        message || data?.error || `El Hub respondió HTTP ${response.status}.`,
      );
      error.response = { status: response.status, data };
      throw error;
    }
    return { data, status: response.status };
  } catch (error) {
    if (error?.name === 'AbortError' || /signal is aborted/i.test(
      String(error?.message || ''),
    )) {
      const timeoutError = new Error(
        'El Local Hub sigue procesando la recuperación. Espera unos segundos y actualiza el estado.',
      );
      timeoutError.code = 'LOCAL_HUB_TIMEOUT';
      throw timeoutError;
    }
    if (
      !error?.response
      && /failed to fetch|networkerror|network error/i.test(
        String(error?.message || ''),
      )
    ) {
      const transportError = new Error(
        'El navegador no ha podido acceder a la red local. Revisa el permiso de red local para EWU.',
      );
      transportError.code = 'LOCAL_NETWORK_ACCESS_REQUIRED';
      throw transportError;
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
}

export async function requestLocalHub(
  store, discovery, scopes, config, refreshGrant = false,
) {
  const send = async (renew) => {
    const token = await tokenFor(store, discovery, scopes, renew);
    return fetchLocalHub(discovery, config, token);
  };
  try {
    return await send(refreshGrant);
  } catch (error) {
    const detail = error?.response?.data?.detail;
    const code = typeof detail === 'object' ? detail.code : detail;
    if (
      !refreshGrant
      && navigator.onLine
      && error?.response?.status === 403
      && ['LOCAL_SESSION_SCOPE_DENIED', 'LOCAL_SESSION_REVOKED'].includes(code)
    ) {
      scopedSession = null;
      return send(true);
    }
    throw error;
  }
}

export async function getLocalModeContext(store, discovery) {
  const response = await requestLocalHub(store, discovery, ['control:connect'], {
    method: 'get', url: '/local/v1/mode/context',
  });
  return response.data;
}

export async function prepareLocalMode(store, discovery) {
  scopedSession = null;
  const response = await requestLocalHub(
    store, discovery, ['local:write:real', 'local-mode:manage'],
    { method: 'post', url: '/local/v1/mode/prepare', data: {} },
  );
  return response.data;
}

export async function acknowledgeLocalMode(store, discovery, message, readiness = {}) {
  const response = await requestLocalHub(store, discovery, ['local:write:real'], {
    method: 'post',
    url: '/local/v1/mode/prepare-ack',
    data: {
      transition_id: message.transition_id,
      ready: !readiness.reason && !readiness.cloud_reachable,
      cloud_reachable: Boolean(readiness.cloud_reachable),
      last_cloud_request_completed_at: new Date().toISOString(),
      last_known_cloud_sequence: Number(message.base_cloud_sequence || 0),
      local_protocol_version: 1,
      reason: readiness.reason || null,
    },
  });
  return response.data;
}

export async function commitLocalMode(store, discovery, transitionId) {
  const response = await requestLocalHub(
    store, discovery, ['local:write:real', 'local-mode:manage'],
    {
      method: 'post',
      url: `/local/v1/mode/${transitionId}/commit`,
      data: {},
    },
  );
  return response.data;
}

export async function readLocalMode(store, discovery, sessionUuid, contract) {
  const response = await requestLocalHub(store, discovery, ['local:write:real'], {
    method: 'get',
    url: `/local/v1/mode/read/${contract}`,
    params: { session_uuid: sessionUuid },
  });
  return response.data;
}

export async function sendLocalModeCommand(store, discovery, command) {
  activeLocalRequests += 1;
  try {
    const response = await requestLocalHub(store, discovery, ['local:write:real'], {
      method: 'post', url: '/local/v1/commands', data: command,
    });
    return response.data;
  } finally {
    activeLocalRequests = Math.max(0, activeLocalRequests - 1);
  }
}

export async function recoverLocalModeEvents(
  store, discovery, sessionUuid, afterSequence = 0,
) {
  const response = await requestLocalHub(store, discovery, ['local:write:real'], {
    method: 'get',
    url: '/local/v1/mode/events',
    params: { session_uuid: sessionUuid, after_sequence: afterSequence },
  });
  return response.data;
}

export function clearLocalModeTransportSession() {
  scopedSession = null;
}

export async function getLocalRecovery(store, discovery) {
  const response = await requestLocalHub(store, discovery, ['control:connect'], {
    method: 'get', url: '/local/v1/recovery',
  });
  return response.data;
}

async function recoveryManagerRequest(store, discovery, path, data = {}) {
  const response = await requestLocalHub(
    store, discovery, ['local:write:real', 'local-mode:manage'],
    {
      method: 'post',
      url: path,
      data,
      timeout: RECOVERY_REQUEST_TIMEOUT_MS,
    },
  );
  return response.data;
}

export function startLocalRecovery(store, discovery) {
  return recoveryManagerRequest(store, discovery, '/local/v1/recovery/start');
}

export function pauseLocalRecovery(store, discovery) {
  return recoveryManagerRequest(store, discovery, '/local/v1/recovery/pause');
}

export function resumeLocalRecovery(store, discovery) {
  return recoveryManagerRequest(store, discovery, '/local/v1/recovery/resume');
}

export function syncLocalRecovery(store, discovery) {
  return recoveryManagerRequest(store, discovery, '/local/v1/recovery/sync');
}

export function verifyLocalRecovery(store, discovery) {
  return recoveryManagerRequest(store, discovery, '/local/v1/recovery/verify');
}

export function prepareCloudReturn(store, discovery) {
  return recoveryManagerRequest(
    store, discovery, '/local/v1/recovery/return/prepare',
  );
}

export function abortCloudReturn(store, discovery) {
  return recoveryManagerRequest(
    store, discovery, '/local/v1/recovery/return/abort',
  );
}

export function commitCloudReturn(store, discovery) {
  return recoveryManagerRequest(
    store, discovery, '/local/v1/recovery/return/commit',
  );
}

export function resolveLocalRecoveryConflict(
  store, discovery, conflictUuid, action,
) {
  return recoveryManagerRequest(
    store,
    discovery,
    `/local/v1/recovery/conflicts/${conflictUuid}/resolve`,
    { action },
  );
}

export async function acknowledgeCloudReturn(
  store, discovery, message, readiness = {},
) {
  const response = await requestLocalHub(store, discovery, ['local:write:real'], {
    method: 'post',
    url: '/local/v1/recovery/return/prepare-ack',
    data: {
      transition_id: message.transition_id,
      ready: !readiness.reason && activeLocalRequests === 0,
      last_local_event_sequence: Number(readiness.lastLocalEventSequence || 0),
      pending_requests: activeLocalRequests,
      reason: readiness.reason || null,
    },
  });
  return response.data;
}

export async function waitForLocalRequests(timeoutMs = 8000) {
  const deadline = Date.now() + timeoutMs;
  while (activeLocalRequests > 0 && Date.now() < deadline) {
    await new Promise((resolve) => window.setTimeout(resolve, 25));
  }
  return activeLocalRequests === 0;
}
